import React, { createContext, useContext, useMemo, useEffect } from 'react'

import { useTranslation } from 'react-i18next'

import {
  FIELD_TYPE_OPERATORS,
  hasDuplicateEnumTitles,
  HIDDEN_FILTER_ATTRIBUTES,
  isMultiValueOperator,
  OPERATOR_DEFINITIONS,
  OperatorType,
} from '../config'
import { getDataClassByReference } from './getReferenceDataClass'
import { resolveEntityDataClass } from './resolveEntityDataClass'
import { FilterConditionRowProps, FilterConditionContextValue } from './types'
import { ReferenceFilter } from '@/Data/typesV2'
import { useAsyncSelect } from '@/hooks/useAsyncSelect'
import { useEntitySchema } from '@/hooks/useEntitySchema'
import {
  getSchemaAttributes,
  getVisibleAttributes,
} from '@/utils/CRUD/read/schema'
import { getSchemaName } from '@/utils/dataClassMap'

/**
 * Context for FilterConditionRow subcomponents
 */
const FilterConditionContext = createContext<
  FilterConditionContextValue | undefined
>(undefined)

/**
 * Hook to access FilterConditionContext
 */
export const useFilterConditionContext = (): FilterConditionContextValue => {
  const context = useContext(FilterConditionContext)
  if (!context) {
    throw new Error(
      'useFilterConditionContext must be used within FilterConditionProvider',
    )
  }
  return context
}

/**
 * Provider component that wraps FilterConditionRow subcomponents
 * Provides all shared state and handlers via context
 */
export const FilterConditionProvider: React.FC<
  FilterConditionRowProps & { children: React.ReactNode }
> = ({
  condition,
  groupId,
  baseEntityName,
  baseSchemaAttributes,
  availableEntities,
  isFirstRow = false,
  hasFieldError,
  hasValueError,
  onClearFieldError,
  onClearValueError,
  onEntityChange,
  onFieldChange,
  onOperatorChange,
  onValueChange,
  onRemoveCondition,
  children,
}) => {
  const { t } = useTranslation()
  const conditionEntity =
    condition.entity || availableEntities[0]?.value || 'person'
  const { attributes: schemaAttributes, isLoading: isLoadingSchema } =
    useEntitySchema(conditionEntity)

  const isMainEntity = conditionEntity === baseEntityName

  const entityDataClass = useMemo(
    () =>
      resolveEntityDataClass(
        conditionEntity,
        baseEntityName,
        condition,
        availableEntities,
        baseSchemaAttributes ?? null,
      ),
    [
      conditionEntity,
      baseEntityName,
      condition,
      availableEntities,
      baseSchemaAttributes,
    ],
  )

  const { loadOptions, reactAsyncSelectStyle } = useAsyncSelect(
    entityDataClass,
    schemaAttributes?.[condition.referenceField || '']?.referenceFilter,
  )

  // Setup async select for reference field selection
  const referenceAttribute = schemaAttributes?.[condition.field]
  const referenceDataClass = useMemo(() => {
    if (
      !referenceAttribute ||
      referenceAttribute.type !== 'reference' ||
      !referenceAttribute.referenceTo
    ) {
      return null
    }
    return getDataClassByReference(
      referenceAttribute.referenceTo,
      referenceAttribute.referenceFilter as ReferenceFilter | undefined,
    )
  }, [referenceAttribute])

  const {
    loadOptions: loadReferenceOptions,
    reactAsyncSelectStyle: referenceAsyncSelectStyle,
  } = useAsyncSelect(referenceDataClass, referenceAttribute?.referenceFilter)

  // Field options for this condition's entity
  const fieldOptions = useMemo(() => {
    if (!schemaAttributes) return []

    // Get schema structure to determine visible attributes based on attributeOrder
    // This ensures filter attributes align with what's shown in the drawer/forms
    // Note: conditionEntity is capitalized (e.g. "Company"), but getSchemaAttributes needs lowercase
    const schemaName = getSchemaName(conditionEntity)
    const { schemaGroups, groupsOrder } = getSchemaAttributes(schemaName)
    const { visibleAttributes } = getVisibleAttributes(
      schemaGroups,
      groupsOrder,
      schemaAttributes,
    )

    // Fallback: if no attributeOrder configured, use all attributes (backward compatibility)
    const useAttributeOrderFilter = visibleAttributes.length > 0

    const standardFields = Object.entries(schemaAttributes)
      .filter(([key, attr]) => {
        // Primary filter: Must be in attributeOrder (if configured)
        if (useAttributeOrderFilter && !visibleAttributes.includes(key)) {
          return false
        }

        // Secondary filters (maintain existing logic)
        if (attr.hidden) return false
        if (HIDDEN_FILTER_ATTRIBUTES.includes(key)) return false
        if (isMainEntity && attr.type === 'reference') return false

        return true
      })
      .map(([key, attr]) => ({
        value: key,
        label: attr.title || key,
        type: attr.type,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))

    // Add special _id field only for referenced entities
    if (!isMainEntity) {
      return [
        {
          value: '_id',
          label: t('filter.record'),
          type: '_id',
        },
        ...standardFields,
      ]
    }

    return standardFields
  }, [schemaAttributes, conditionEntity, isMainEntity, t])

  // Auto-select first field option when field is empty for referenced entities
  useEffect(() => {
    const firstOption = fieldOptions[0]
    if (!isMainEntity && !condition.field && firstOption && !isLoadingSchema) {
      onFieldChange(groupId, condition.id!, firstOption.value)
    }
  }, [
    condition.field,
    condition.id,
    fieldOptions,
    groupId,
    isLoadingSchema,
    isMainEntity,
    onFieldChange,
  ])

  // Operators for the selected field
  const availableOperators = useMemo(() => {
    // Handle special _id field
    if (condition.field === '_id') {
      const operators = FIELD_TYPE_OPERATORS['reference']
      return (
        operators?.map((op) => ({
          ...OPERATOR_DEFINITIONS[op],
          label: t(OPERATOR_DEFINITIONS[op].labelKey),
        })) || []
      )
    }

    const attribute = schemaAttributes?.[condition.field]
    if (!attribute) return []

    const operators =
      FIELD_TYPE_OPERATORS[attribute.type] || FIELD_TYPE_OPERATORS['string']
    if (!operators) return []
    return operators
      .filter(
        (op) =>
          attribute.type !== 'enum' ||
          !hasDuplicateEnumTitles(attribute.enumValues) ||
          (op !== 'eq' && op !== 'neq'),
      )
      .map((op) => ({
        ...OPERATOR_DEFINITIONS[op],
        label: t(OPERATOR_DEFINITIONS[op].labelKey),
      }))
  }, [schemaAttributes, condition.field, t])

  // Auto-select first operator when current operator is not available
  useEffect(() => {
    const firstOperator = availableOperators[0]
    if (condition.field && firstOperator) {
      const currentOperatorValid = availableOperators.some(
        (op) => op.value === condition.operator,
      )
      if (!currentOperatorValid) {
        onOperatorChange(groupId, condition.id!, firstOperator.value)
      }
    }
  }, [
    condition.field,
    condition.id,
    condition.operator,
    availableOperators,
    groupId,
    onOperatorChange,
  ])

  // Input type for the value field
  const inputType = useMemo(() => {
    const operatorDef = OPERATOR_DEFINITIONS[condition.operator as OperatorType]
    if (!operatorDef?.requiresValue) return 'none'
    const operator = condition.operator as OperatorType
    const isMultiOperator = isMultiValueOperator(operator)

    // Handle special _id field - show entity selector
    if (condition.field === '_id') {
      return isMultiOperator ? 'entity-selector-multi' : 'entity-selector'
    }

    const attribute = schemaAttributes?.[condition.field]

    // Handle reference fields
    if (attribute?.type === 'reference') {
      return isMultiOperator ? 'reference-selector-multi' : 'reference-selector'
    }

    // Check if operator defines an inputType
    if (operatorDef?.inputType) return operatorDef.inputType

    if (!attribute) return 'text'

    switch (attribute.type) {
      case 'date':
      case 'datetime':
        return 'datetime'
      case 'integer':
        return 'number'
      case 'boolean':
        return 'boolean'
      case 'enum':
        return 'select'
      default:
        return 'text'
    }
  }, [schemaAttributes, condition.field, condition.operator])

  // Get attribute for current field
  const attribute = schemaAttributes?.[condition.field]

  // Handlers wrapped to match context signature
  const handleEntityChange = (entity: string, referenceField?: string) => {
    onEntityChange(groupId, condition.id!, entity, referenceField)
  }

  const handleFieldChange = (field: string) => {
    onFieldChange(groupId, condition.id!, field)
  }

  const handleOperatorChange = (operator: string) => {
    onOperatorChange(groupId, condition.id!, operator)
  }

  const handleValueChange = (value: unknown, label?: string) => {
    onValueChange(groupId, condition.id!, value, label)
  }

  const handleRemove = (e?: React.MouseEvent) => {
    onRemoveCondition(groupId, condition.id!, e)
  }

  const handleClearFieldError = () => {
    onClearFieldError?.()
  }

  const handleClearValueError = () => {
    onClearValueError?.()
  }

  const contextValue: FilterConditionContextValue = {
    condition,
    groupId,
    conditionEntity,
    isMainEntity,
    isFirstRow,
    schemaAttributes,
    baseSchemaAttributes,
    isLoadingSchema,
    availableEntities,
    fieldOptions,
    availableOperators,
    inputType,
    attribute,
    entityDataClass,
    referenceDataClass,
    loadOptions,
    loadReferenceOptions,
    reactAsyncSelectStyle,
    referenceAsyncSelectStyle,
    hasFieldError,
    hasValueError,
    handleEntityChange,
    handleFieldChange,
    handleOperatorChange,
    handleValueChange,
    handleRemove,
    handleClearFieldError,
    handleClearValueError,
  }

  return (
    <FilterConditionContext.Provider value={contextValue}>
      {children}
    </FilterConditionContext.Provider>
  )
}
